/**
 * Soft vs hard tool-failure classification for chat chrome.
 * Soft = recoverable miss (wrong id, no captions, allowlist) - muted UI, not alarm red.
 * Hard = unexpected / blocking failures - keep warning weight.
 */

export function toolResultFailureText(toolResult: unknown): string | null {
  if (toolResult == null || typeof toolResult !== 'object') return null
  const tr = toolResult as Record<string, unknown>
  const err = tr.error != null ? String(tr.error).trim() : ''
  if (err) return err
  const msg = tr.message != null ? String(tr.message).trim() : ''
  if (tr.success === false && msg) return msg
  return null
}

function hostLabel(url: unknown): string | null {
  try {
    const host = new URL(String(url || '')).hostname.replace(/^www\./, '')
    return host || null
  } catch {
    return null
  }
}

function isImageLoginHost(url: unknown): boolean {
  const host = hostLabel(url)
  if (!host) return false
  return host === 'zight.com' || host.endsWith('.zight.com') || host.endsWith('.basecamp.com')
}

/** Login walls and repeat-fetch blocks. Quiet footnote, never the machine error code. */
export function loginWallDisplayText(toolResult: unknown): string | null {
  if (toolResult == null || typeof toolResult !== 'object') return null
  const tr = toolResult as Record<string, unknown>
  const code = tr.error_code != null ? String(tr.error_code) : ''
  const err = tr.error != null ? String(tr.error) : ''
  const isImageCode = code === 'IMAGE_REQUIRES_USER_PASTE' || err.includes('IMAGE_REQUIRES_USER_PASTE')
  const isPageCode = code === 'PAGE_REQUIRES_LOGIN'
  const isAlready = code === 'IMAGE_ALREADY_ATTACHED' || /already attached/i.test(err)
  const isDenied = code === 'FETCH_URL_DENIED'
  if (!isImageCode && !isPageCode && !isAlready && !isDenied) return null

  const host = hostLabel(tr.url)
  if (isAlready && !isDenied) {
    return host
      ? `${host} is behind a login. The pasted screenshot is already in this chat.`
      : 'That link is behind a login. The pasted screenshot is already in this chat.'
  }
  if (isDenied && isAlready) {
    return 'That link already failed. The pasted screenshot is already in this chat.'
  }
  if (isDenied) return 'That link already failed and was not opened again.'
  if (isPageCode || (isImageCode && !isImageLoginHost(tr.url))) {
    return host
      ? `${host} is behind a login, so the page was not opened.`
      : 'This page is behind a login, so it was not opened.'
  }
  if (isImageCode) {
    return 'This image is behind a login. Paste a screenshot if it is not already in the chat.'
  }
  return null
}

export function isAllowlistSoftFailure(toolResult: unknown): boolean {
  if (toolResult == null || typeof toolResult !== 'object') return false
  const tr = toolResult as Record<string, unknown>
  if (tr.error_code === 'DOCUMENT_NOT_IN_ALLOWLIST') return true
  const err = tr.error != null ? String(tr.error) : ''
  return err.includes('conversation allowlist')
}

/** Recoverable tool failures - muted chrome; do not paint red alarm cards. */
export function isSoftToolFailure(toolResult: unknown): boolean {
  if (isAllowlistSoftFailure(toolResult)) return true
  if (loginWallDisplayText(toolResult)) return true
  if (toolResult == null || typeof toolResult !== 'object') return false
  const tr = toolResult as Record<string, unknown>
  if (tr.success === true) return false
  const err = tr.error != null ? String(tr.error) : ''
  if (!err.trim() && tr.success !== false) return false

  // Exploration misses (wrong id / stale path) - quiet footnote, not a red banner.
  if (/^not found\.?$/i.test(err.trim())) return true
  if (err.includes('Section not found')) return true
  if (/Document not found/i.test(err)) return true
  if (/Todo not found/i.test(err)) return true
  if (/No captions available/i.test(err)) return true
  if (/Transcript returned empty/i.test(err)) return true
  if (/No artifact found for handle/i.test(err)) return true
  if (tr.tool === 'resolveDocumentIdForWrite') return true
  // Patch/anchor misses - agent usually retries with a fresh read; not a crash.
  if (/old_string not found/i.test(err)) return true
  if (/new_string.*identical|identical to old_string/i.test(err)) return true
  // Unsupported Basecamp recording kinds (todosets, todolists, people, etc.) - not a crash.
  if (/Unrecognised Basecamp URL/i.test(err)) return true
  if (/Invalid todoUrl format/i.test(err)) return true
  // JS-rendered or login-gated pages - expected miss, timeline footnote, not a banner.
  if (/Could not extract meaningful text/i.test(err)) return true
  // Structured resolve failures always include a recovery hint
  if (tr.hint != null && err.trim()) return true
  return false
}

export function isHardToolFailure(toolResult: unknown): boolean {
  return Boolean(toolResultFailureText(toolResult)) && !isSoftToolFailure(toolResult)
}

export function allowlistFailureDisplayText(_toolResult?: unknown): string {
  return 'Could not read yet - try searchDocuments or use the spilled preview handle'
}

/** Short user-facing line for soft failures (quiet context-read style). */
export function softFailureDisplayText(toolResult: unknown): string {
  if (isAllowlistSoftFailure(toolResult)) return allowlistFailureDisplayText(toolResult)
  const loginWall = loginWallDisplayText(toolResult)
  if (loginWall) return loginWall
  const err = toolResultFailureText(toolResult) || 'Unavailable'
  if (/^not found\.?$/i.test(err.trim()) || /Document not found/i.test(err)) {
    return 'Not found'
  }
  if (/Todo not found/i.test(err)) return 'Not found'
  if (/No captions available/i.test(err)) return 'No captions available'
  if (/Transcript returned empty/i.test(err)) return 'Transcript empty'
  if (/No artifact found for handle/i.test(err)) return 'Spill handle not found'
  if (err.includes('Section not found')) return err
  if (/old_string not found/i.test(err)) {
    return err.length > 100 ? `${err.slice(0, 97)}...` : err
  }
  if (/Unrecognised Basecamp URL/i.test(err)) {
    return 'That Basecamp link type is not supported yet (todos, messages, and inbox forwards only)'
  }
  if (/Invalid todoUrl format/i.test(err)) {
    return 'That Basecamp link is not a todo URL'
  }
  // Keep the URL: the Open page row has no other hint about which page failed.
  if (/Could not extract meaningful text/i.test(err)) return err
  return err.length > 100 ? `${err.slice(0, 97)}...` : err
}
